import { Button, Dialog, DialogActions, DialogContent, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useState } from 'react';
import { OnlyFansSubscriber } from '../../../../../../hooks/useSubscriber';
import { SubscribersAdmin } from '../../../../../../hooks/useSubscribers';
import StyledCardTitleBar from '../../../../../common/StyledCardTitleBar';
import SelectTimezone from '../../../../../forms/helpers/SelectTimezone';
import SextforceMetricsCompletedSelector from '../../SextforceMetricsCompletedSelector';
import SextforceMetricsCrossReferenceEarningsSubscriberSelector from '../../SextforceMetricsCrossReferenceEarningsSubscriberSelector';

interface SextforceMetricsTrialsOverviewViewSettingsDialogProps {
    open: boolean;
    onClose: () => void;
    timezone: string;
    setTimezone: (timezone: string) => void;
    trialsLoading: boolean;
    trialsSelectorType: { includeInactive: boolean; type: string };
    setTrialsSelectorType: (type: { includeInactive: boolean; type: string }) => void;
    completedType: boolean | null;
    setCompletedType: (type: boolean | null) => void;
    subscribers: SubscribersAdmin | OnlyFansSubscriber[] | undefined;
    subscribersLoading: boolean;
    crossReferenceSubscriberId: string;
    setCrossReferenceSubscriberId: (id: string) => void;
    amountType: 'gross' | 'net';
    setAmountType: (type: 'gross' | 'net') => void;
}

const SextforceMetricsTrialsOverviewViewSettingsDialog = ({
    open,
    onClose,
    timezone,
    setTimezone,
    trialsLoading,
    trialsSelectorType,
    setTrialsSelectorType,
    completedType,
    setCompletedType,
    subscribers,
    subscribersLoading,
    crossReferenceSubscriberId,
    setCrossReferenceSubscriberId,
    amountType,
    setAmountType,
}: SextforceMetricsTrialsOverviewViewSettingsDialogProps) => {
    // Local state for settings that will be applied when "Apply" is clicked
    const [localTimezone, setLocalTimezone] = useState(timezone);
    const [localTrialsSelectorType, setLocalTrialsSelectorType] = useState(trialsSelectorType);
    const [localCompletedType, setLocalCompletedType] = useState(completedType);
    const [localCrossReferenceSubscriberId, setLocalCrossReferenceSubscriberId] = useState(crossReferenceSubscriberId);
    const [localAmountType, setLocalAmountType] = useState(amountType);

    // Reset local state when dialog opens
    const handleDialogEnter = () => {
        setLocalTimezone(timezone);
        setLocalTrialsSelectorType(trialsSelectorType);
        setLocalCompletedType(completedType);
        setLocalCrossReferenceSubscriberId(crossReferenceSubscriberId);
        setLocalAmountType(amountType);
    };

    // Apply settings and close dialog
    const handleApply = () => {
        setTimezone(localTimezone);
        setTrialsSelectorType(localTrialsSelectorType);
        localStorage.setItem('sextforceMetricsCampaignsOverviewSelectorType', localTrialsSelectorType.includeInactive ? 'true' : 'false');
        setCompletedType(localCompletedType);
        setCrossReferenceSubscriberId(localCrossReferenceSubscriberId);

        setAmountType(localAmountType);
        localStorage.setItem('showEarningsAsGross', localAmountType === 'gross' ? 'true' : 'false');

        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            TransitionProps={{
                onEnter: handleDialogEnter,
            }}
        >
            <StyledCardTitleBar title="View Settings" />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SelectTimezone fullWidth size={'small'} timezone={localTimezone} setTimezone={setLocalTimezone} />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color={'text.secondary'}>
                            Type
                        </Typography>
                        <Grid item xs>
                            <ToggleButtonGroup
                                color="secondary"
                                size={'small'}
                                fullWidth
                                disabled={trialsLoading}
                                exclusive={true}
                                value={localTrialsSelectorType.type}
                                onChange={(e, value: string) => {
                                    setLocalTrialsSelectorType({ includeInactive: localTrialsSelectorType.includeInactive, type: value });
                                }}
                                sx={{ marginTop: '4px', height: '39px' }}
                            >
                                <ToggleButton value="trialLinkTrial" key="center" sx={{ minWidth: '70px' }}>
                                    Trials
                                </ToggleButton>
                                <ToggleButton value="trialLinkPromo" key="right" sx={{ minWidth: '70px' }}>
                                    Promos
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color={'text.secondary'}>
                            Deleted Links
                        </Typography>
                        <ToggleButtonGroup
                            value={localTrialsSelectorType.includeInactive ? 'true' : 'false'}
                            exclusive
                            fullWidth
                            color="secondary"
                            size={'small'}
                            disabled={trialsLoading}
                            onChange={(e, value: string) => {
                                if (!value) {
                                    return;
                                }

                                setLocalTrialsSelectorType({
                                    ...localTrialsSelectorType,
                                    includeInactive: value === 'true',
                                });
                            }}
                            sx={{ marginTop: '4px', height: '39px' }}
                        >
                            <ToggleButton color="secondary" fullWidth value={'false'} sx={{ minWidth: '120px' }}>
                                Hide Deleted
                            </ToggleButton>
                            <ToggleButton color="secondary" fullWidth value={'true'} sx={{ minWidth: '120px' }}>
                                Show Deleted
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color={'text.secondary'}>
                            Completed Promos
                        </Typography>
                        <SextforceMetricsCompletedSelector
                            loading={trialsLoading}
                            completedType={localCompletedType}
                            setCompletedType={setLocalCompletedType}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color={'text.secondary'}>
                            Calculate Cross-Pollination With...
                        </Typography>
                        <SextforceMetricsCrossReferenceEarningsSubscriberSelector
                            subscribers={subscribers}
                            subscribersLoading={subscribersLoading}
                            crossReferenceSubscriberId={localCrossReferenceSubscriberId}
                            setCrossReferenceSubscriberId={setLocalCrossReferenceSubscriberId}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color={'text.secondary'}>
                            Amount Type
                        </Typography>
                        <ToggleButtonGroup
                            value={localAmountType}
                            exclusive
                            color="secondary"
                            size="small"
                            fullWidth
                            onChange={(_event, newValue) => {
                                if (!newValue) return;
                                setLocalAmountType(newValue);
                            }}
                            sx={{ height: '39px' }}
                        >
                            <ToggleButton value="gross" fullWidth sx={{ minWidth: '80px' }}>
                                GROSS
                            </ToggleButton>
                            <ToggleButton value="net" fullWidth sx={{ minWidth: '80px' }}>
                                NET
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                }}
            >
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleApply} color="primary" variant="contained">
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SextforceMetricsTrialsOverviewViewSettingsDialog;
